'use client';

import Script from 'next/script';
import { useEffect, useRef } from 'react';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useConsent } from '@/providers/consent-provider';
import { useSession } from '@/hooks/use-session';
import { setUserProperties, trackSignUpConversion } from '@/lib/analytics';

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID;

/** A signup counts as an Ads conversion only this long after activation. */
const SIGNUP_CONVERSION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

/**
 * The gtag stub and Consent Mode v2 defaults — everything denied — before any
 * other command. The stub queues commands until gtag.js has loaded. Runs once.
 */
function initGtag() {
  if (typeof window.gtag === 'function') return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js reads the `arguments` object itself, not an array copy.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
  window.gtag('js', new Date());
}

function UserIdentifier() {
  const { user } = useUser();

  useEffect(() => {
    if (user?.sub) {
      setUserProperties({ sub: user.sub });
    }
  }, [user?.sub]);

  return null;
}

/**
 * The signup conversion, once per account and browser: an account activated
 * (email verified) within the last week that has not been reported yet. It is
 * marked as reported only once a Google tag actually took it, so a visitor who
 * consents later in that week is still counted.
 *
 * Mounted on its own inside the providers (app/layout.tsx): it reads the
 * session through React Query, which GoogleAnalytics — above QueryProvider,
 * so the tag loads as early as consent allows — cannot.
 */
export function SignUpConversion() {
  const { data: session } = useSession();
  const sub = session?.user?.sub;
  const activatedAt = session?.user?.activatedAt;
  // Re-check when consent arrives: GoogleAnalytics (earlier in the tree, so
  // its effect runs first) has set up the tag by then.
  const { granted: analyticsConsent } = useConsent('googleAnalytics');
  const { granted: adsConsent } = useConsent('googleAds');

  useEffect(() => {
    if (!sub || !activatedAt || typeof window.gtag !== 'function') return;
    if (Date.now() - Date.parse(activatedAt) > SIGNUP_CONVERSION_WINDOW_MS) return;
    const key = `fd_conv_signup_${sub}`;
    try {
      if (localStorage.getItem(key)) return;
      trackSignUpConversion();
      localStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage unavailable (private mode): report nothing rather than repeat.
    }
  }, [sub, activatedAt, analyticsConsent, adsConsent]);

  return null;
}

/**
 * The Google tag (gtag.js) for Google Analytics 4 and Google Ads, each behind
 * its own consent in the cookie banner (Klaro services googleAnalytics and
 * googleAds). Nothing loads before one of them is granted; Consent Mode v2
 * signals follow the banner choice, and each product is configured only with
 * its own consent.
 *
 * Consent Mode v2: everything is denied by default, before any other gtag
 * call. analytics_storage follows the analytics consent; ad_storage and
 * ad_user_data follow the marketing consent (Google Ads service) — whether or
 * not an Ads tag ID is set, since conversions imported from GA4 need them
 * too. Both are sent again on withdrawal.
 *
 * Ads is conversion tracking only: ad_personalization stays denied and both
 * products run without ad-personalization signals or Google signals, so no
 * remarketing audiences are built.
 *
 * Page views: the GA config sends the first one, and GA4's enhanced
 * measurement counts client-side navigations from the browser history. There
 * is deliberately no route tracker of our own — with enhanced measurement on
 * (GA4's default) it counted every in-app navigation twice.
 */
export function GoogleAnalytics() {
  const { granted: analyticsConsent } = useConsent('googleAnalytics');
  const { granted: adsConsent } = useConsent('googleAds');
  const analytics = !!GA_MEASUREMENT_ID && analyticsConsent;
  const ads = !!ADS_ID && adsConsent;
  // The ad consent signals follow the banner, not whether an Ads tag is set.
  const marketing = adsConsent;
  const configured = useRef(new Set<string>());

  useEffect(() => {
    if (!analytics && !ads) {
      // Withdrawn after the tag loaded: tell it, rather than fall silent.
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
        });
      }
      return;
    }
    initGtag();
    window.gtag('consent', 'update', {
      analytics_storage: analytics ? 'granted' : 'denied',
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      // Conversion tracking only, never remarketing.
      ad_personalization: 'denied',
    });
    if (analytics && GA_MEASUREMENT_ID && !configured.current.has(GA_MEASUREMENT_ID)) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      configured.current.add(GA_MEASUREMENT_ID);
    }
    if (ads && ADS_ID && !configured.current.has(ADS_ID)) {
      window.gtag('config', ADS_ID, { allow_ad_personalization_signals: false });
      configured.current.add(ADS_ID);
    }
  }, [analytics, ads, marketing]);

  if (!analytics && !ads) return null;

  return (
    <>
      <Script
        // One library load whichever products are on; what is measured is
        // decided by the config calls above, not by this id.
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID || ADS_ID}`}
        strategy="afterInteractive"
      />
      {analytics && <UserIdentifier />}
    </>
  );
}
